<template>
    <div class="canvas-container">
        <!-- Barra de modos moderna -->
        <div class="mode-bar">
            <button
:class="['mode-btn', { active: mode === 'draw' }]" title="Modo dibujo"
                @click.prevent="mode = 'draw'">
                <i class="fas fa-pen"></i>
                <span>Dibujo</span>
            </button>
            <button
:class="['mode-btn', { active: mode === 'move' }]" title="Mover y editar elementos"
                @click.prevent="mode = 'move'">
                <i class="fas fa-arrows-alt"></i>
                <span>Mover</span>
            </button>
            <button
:class="['mode-btn', { active: mode === 'text' }]" title="Agregar texto"
                @click.prevent="mode = 'text'">
                <i class="fas fa-font"></i>
                <span>Texto</span>
            </button>
        </div>

        <!-- Canvas principal -->
        <div class="canvas-wrapper">
            <canvas
ref="canvas" @mousedown="handleMouseDown" @mousemove="handleMouseMove" @mouseup="handleMouseUp"
                @mouseleave="handleMouseUp" @dblclick="handleDoubleClick">
            </canvas>
        </div>

        <!-- Controles modernos -->
        <div class="brush-controls">
            <!-- Controles de dibujo/texto -->
            <div class="controls-left">
                <div class="control-item">
                    <label>Color</label>
                    <div class="color-input-wrapper">
                        <input id="color" v-model="strokeStyle" type="color" class="color-picker" />
                        <span class="color-hex">{{ strokeStyle }}</span>
                    </div>
                </div>

                <div v-if="mode === 'draw'" class="control-item">
                    <label>Grosor</label>
                    <div class="slider-wrapper">
                        <input
v-model.number="lineWidth" type="range" min="0.5" max="50" step="0.5"
                            class="line-slider" />
                        <span class="value-display">{{ lineWidth }}px</span>
                    </div>
                </div>

                <div v-if="mode === 'text'" class="control-item">
                    <label>Tamaño</label>
                    <div class="slider-wrapper">
                        <input v-model.number="fontSize" type="range" min="12" max="72" step="1" class="line-slider" />
                        <span class="value-display">{{ fontSize }}px</span>
                    </div>
                </div>

                <div v-if="mode === 'text'" class="control-item">
                    <label>Fuente</label>
                    <select v-model="fontFamily" class="font-select">
                        <option value="Arial">Arial</option>
                        <option value="Helvetica">Helvetica</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Verdana">Verdana</option>
                        <option value="Courier New">Courier New</option>
                    </select>
                </div>
            </div>

            <!-- Botones de acción -->
            <div class="action-buttons">
                <button
class="action-btn" title="Deshacer (Ctrl+Z)" :disabled="shapes.length === 0"
                    @click.prevent="undo">
                    <i class="fas fa-undo"></i>
                </button>
                <button
class="action-btn" title="Rehacer (Ctrl+Y)" :disabled="undoneShapes.length === 0"
                    @click.prevent="redo">
                    <i class="fas fa-redo"></i>
                </button>
                <div class="divider"></div>
                <button class="action-btn danger" title="Limpiar todo" @click.prevent="clearCanvas">
                    <i class="fas fa-trash-alt"></i>
                </button>
                <div class="divider"></div>
                <button class="action-btn primary" title="Guardar cambios" @click.prevent="saveImage">
                    <i class="fas fa-save"></i>
                </button>
                <button class="action-btn" title="Descargar imagen" @click.prevent="downloadImage">
                    <i class="fas fa-download"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, defineProps, watch, inject } from 'vue'

const Swal = inject('$swal')

// Prop para recibir el dibujo inicial desde el padre
const props = defineProps({
    initialShapes: {
        type: Object,
        default: () => ({})
    },
    imagepath: {
        type: String,
        default: ''
    }
})

const emit = defineEmits(['saveShapes'])

// -- Referencia y contexto --
const canvas = ref(null)
let ctx = null

// -- Configuración de dibujo --
const strokeStyle = ref('#000000')
const lineWidth = ref(4)

// -- Afinado del trazo --
const minPointGap = 0.6
const smoothingFactor = 0.35

// -- Configuración de texto --
const fontSize = ref(18)
const fontFamily = ref('Arial')

// -- Densidad de píxeles (pantallas retina) --
const dpr = window.devicePixelRatio || 1

// -- Imagen de fondo (opcional) --
const backgroundImage = new Image()
backgroundImage.src = props.imagepath

// -- Modos: "draw", "move", "text" --
const mode = ref('draw')

// -- Estructura para almacenar formas (shapes) --
const shapes = ref([])
const undoneShapes = ref([])

// -- Variables para el trazo en curso (solo en modo draw) --
let isDrawing = false
let currentStroke = []

// -- Variables para mover shapes --
let isDragging = false
let selectedShape = null
// offset para arrastrar
let offsetX = 0
let offsetY = 0

// -- Tolerancia para selección de boundingBox --
const selectionTolerance = 10

function resizeCanvas() {
    const canvasEl = canvas.value
    if (!canvasEl) return

    const parent = canvasEl.parentElement
    const width = parent.clientWidth
    const height = parent.clientHeight

    canvasEl.width = width * dpr
    canvasEl.height = height * dpr
    canvasEl.style.width = width + 'px'
    canvasEl.style.height = height + 'px'

    ctx = canvasEl.getContext('2d')
    ctx.scale(dpr, dpr)

    redrawCanvas()
}

onMounted(() => {
    resizeCanvas()

    window.addEventListener('resize', resizeCanvas)

    backgroundImage.onload = () => {
        shapes.value = Array.isArray(props.initialShapes) ? [...props.initialShapes] : [];
        redrawCanvas()
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', resizeCanvas)
})

watch(() => props.initialShapes, (newShapes) => {
    // Limpiar estado previo
    shapes.value = Array.isArray(newShapes) ? [...newShapes] : [];
    undoneShapes.value = [];
    selectedShape = null;
    isDrawing = false;
    isDragging = false;
    redrawCanvas();
}, { deep: true });

/** Mousedown: según el modo, iniciamos dibujo o arrastre/edición. */
function handleMouseDown(e) {
    const { x, y } = getCanvasCoordinates(e)

    if (mode.value === 'draw') {
        isDrawing = true
        currentStroke = []
        undoneShapes.value = []
        pushStrokePoint(x, y, { forceRaw: true })
    }
    else if (mode.value === 'move') {
        // Comprobamos si clic en algún shape (de delante hacia atrás)
        for (let i = shapes.value.length - 1; i >= 0; i--) {
            const shp = shapes.value[i]
            if (hitTestShape(x, y, shp)) {
                // Seleccionamos este shape
                selectedShape = shp
                isDragging = true
                // Llevamos shape al frente
                shapes.value.splice(i, 1)
                shapes.value.push(shp)

                if (shp.type === 'freehand') {
                    // offset = diferencia entre clic y esquina sup izq
                    offsetX = x - shp.boundingBox.minX
                    offsetY = y - shp.boundingBox.minY
                } else if (shp.type === 'text') {
                    // offset = diferencia entre clic y (x, y) del texto
                    offsetX = x - shp.x
                    offsetY = y - shp.y
                }
                redrawCanvas()
                return
            }
        }
        // Si no se hace clic en ningún shape, deseleccionamos
        selectedShape = null
        redrawCanvas()
    }
    else if (mode.value === 'text') {
        // Insertar texto con SweetAlert2
        Swal.fire({
            title: 'Agregar Texto',
            input: 'textarea',
            inputPlaceholder: 'Escribe aquí tu texto...',
            inputAttributes: {
                'aria-label': 'Escribe tu texto',
                'style': 'min-height: 120px; font-size: 15px;'
            },
            showCancelButton: true,
            confirmButtonText: '<i class="fas fa-check"></i> Agregar',
            cancelButtonText: '<i class="fas fa-times"></i> Cancelar',
            confirmButtonColor: '#3b82f6',
            cancelButtonColor: '#6b7280',
            reverseButtons: true,
            backdrop: true,
            allowOutsideClick: true,
            focusConfirm: false,
            inputValidator: (value) => {
                if (!value || value.trim() === '') {
                    return '¡Por favor escribe algo!'
                }
            },
            customClass: {
                popup: 'modern-swal-popup',
                title: 'modern-swal-title',
                input: 'modern-swal-input',
                confirmButton: 'modern-swal-confirm',
                cancelButton: 'modern-swal-cancel',
                actions: 'modern-swal-actions'
            }
        }).then((result) => {
            if (result.isConfirmed && result.value) {
                undoneShapes.value = []
                // Crear shape de texto con configuración moderna
                const newTextShape = {
                    type: 'text',
                    text: result.value,
                    x,
                    y,
                    font: `${fontSize.value}px ${fontFamily.value}`,
                    color: strokeStyle.value
                }
                updateTextBoundingBox(newTextShape)
                shapes.value.push(newTextShape)
                redrawCanvas()

                // Confirmación visual
                Swal.fire({
                    icon: 'success',
                    title: '¡Texto agregado!',
                    showConfirmButton: false,
                    timer: 1500,
                    customClass: {
                        popup: 'modern-swal-success'
                    }
                })
            }
        })
    }
}

/** Mousemove: dibujar o arrastrar un shape. */
function handleMouseMove(e) {
    const { x, y } = getCanvasCoordinates(e)

    // -- Modo dibujo --
    if (mode.value === 'draw' && isDrawing) {
        pushStrokePoint(x, y)
        redrawCanvas()
    }

    // -- Modo mover --
    if (mode.value === 'move' && isDragging && selectedShape) {
        if (selectedShape.type === 'freehand') {
            // Calcular nuevo minX, minY
            const newMinX = x - offsetX
            const newMinY = y - offsetY
            const dx = newMinX - selectedShape.boundingBox.minX
            const dy = newMinY - selectedShape.boundingBox.minY

            // Mover todos los puntos
            selectedShape.points.forEach(pt => {
                pt.x += dx
                pt.y += dy
            })
            updateFreehandBoundingBox(selectedShape)
        }
        else if (selectedShape.type === 'text') {
            // mover texto
            selectedShape.x = x - offsetX
            selectedShape.y = y - offsetY
            updateTextBoundingBox(selectedShape)
        }
        redrawCanvas()
    }
}

/** Mouseup: termina el dibujo o arrastre. */
function handleMouseUp(e) {
    if (mode.value === 'draw' && isDrawing) {
        const releaseCoords = e ? getCanvasCoordinates(e) : null
        if (releaseCoords) {
            pushStrokePoint(releaseCoords.x, releaseCoords.y, { forceRaw: true })
        }
        isDrawing = false
        // Guardar el shape
        const newShape = {
            type: 'freehand',
            points: [...currentStroke],
            strokeStyle: strokeStyle.value,
            lineWidth: lineWidth.value
        }
        updateFreehandBoundingBox(newShape)
        shapes.value.push(newShape)
        currentStroke = []
        redrawCanvas()
    }

    if (mode.value === 'move' && isDragging) {
        isDragging = false
    }
}

/** Doble clic: si es texto y está seleccionado, permitir editar. */
function handleDoubleClick() {
    if (mode.value !== 'move') return // Solo en modo mover
    if (!selectedShape || selectedShape.type !== 'text') return

    // Editar texto con SweetAlert2
    Swal.fire({
        title: 'Editar Texto',
        input: 'textarea',
        inputValue: selectedShape.text,
        inputPlaceholder: 'Edita tu texto...',
        inputAttributes: {
            'aria-label': 'Edita tu texto',
            'style': 'min-height: 120px; font-size: 15px;'
        },
        showCancelButton: true,
        confirmButtonText: '<i class="fas fa-check"></i> Actualizar',
        cancelButtonText: '<i class="fas fa-times"></i> Cancelar',
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#6b7280',
        reverseButtons: true,
        backdrop: true,
        allowOutsideClick: true,
        focusConfirm: false,
        inputValidator: (value) => {
            if (!value || value.trim() === '') {
                return '¡El texto no puede estar vacío!'
            }
        },
        customClass: {
            popup: 'modern-swal-popup',
            title: 'modern-swal-title',
            input: 'modern-swal-input',
            confirmButton: 'modern-swal-confirm',
            cancelButton: 'modern-swal-cancel',
            actions: 'modern-swal-actions'
        }
    }).then((result) => {
        if (result.isConfirmed && result.value) {
            selectedShape.text = result.value
            updateTextBoundingBox(selectedShape)
            redrawCanvas()

            // Confirmación visual
            Swal.fire({
                icon: 'success',
                title: '¡Texto actualizado!',
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                    popup: 'modern-swal-success'
                }
            })
        }
    })
}

/** Limpia el canvas y redibuja todo. */
function redrawCanvas() {
    if (!canvas.value) return
    const canvasWidth = canvas.value.width / dpr
    const canvasHeight = canvas.value.height / dpr
    ctx.clearRect(0, 0, canvasWidth, canvasHeight)

    // Dibujar imagen centrada manteniendo proporción
    if (backgroundImage.complete && backgroundImage.naturalWidth > 0) {
        const imgAspect = backgroundImage.naturalWidth / backgroundImage.naturalHeight
        const canvasAspect = canvasWidth / canvasHeight

        let drawWidth, drawHeight, offsetX, offsetY

        if (imgAspect > canvasAspect) {
            // La imagen es más ancha proporcionalmente
            drawWidth = canvasWidth
            drawHeight = canvasWidth / imgAspect
            offsetX = 0
            offsetY = (canvasHeight - drawHeight) / 2
        } else {
            // La imagen es más alta proporcionalmente
            drawHeight = canvasHeight
            drawWidth = canvasHeight * imgAspect
            offsetX = (canvasWidth - drawWidth) / 2
            offsetY = 0
        }

        ctx.drawImage(backgroundImage, offsetX, offsetY, drawWidth, drawHeight)
    }

    // Dibuja shapes
    for (const shp of shapes.value) {
        drawShape(shp)
    }
    // Dibuja el trazo actual (si estamos dibujando)
    if (currentStroke.length > 0) {
        drawStroke(currentStroke)
    }

    // Si hay un shape seleccionado, dibujar su bounding box
    if (selectedShape) {
        drawBoundingBox(selectedShape.boundingBox)
    }
}

/** Dibuja un shape (freehand o text). */
function drawShape(shp) {
    if (shp.type === 'freehand') {
        drawStroke(shp.points, shp)
    } else if (shp.type === 'text') {
        ctx.save()
        ctx.fillStyle = shp.color
        ctx.font = shp.font
        ctx.fillText(shp.text, shp.x, shp.y)
        ctx.restore()
    }
}

/** Dibuja un trazo (suavizado) a partir de puntos. */
function drawStroke(pointsArray, shape = {}) {
    if (pointsArray.length < 2) return

    const width = Number(shape.lineWidth || lineWidth.value) || 1
    const epsilon = Math.max(0.35, Math.min(1.6, width * 0.18))
    const simplifiedPoints = simplifyDouglasPeucker(pointsArray, epsilon)
    if (simplifiedPoints.length < 2) return
    const curves = catmullRom2bezier(simplifiedPoints)

    ctx.strokeStyle = shape.strokeStyle || strokeStyle.value
    ctx.lineWidth = width
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    ctx.beginPath()
    ctx.moveTo(simplifiedPoints[0].x, simplifiedPoints[0].y)
    for (const curve of curves) {
        const { c1, c2, p2 } = curve
        ctx.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, p2.x, p2.y)
    }
    ctx.stroke()
}

/** Dibuja el rectángulo de selección alrededor del shape. */
function drawBoundingBox(box) {
    if (!box) return
    ctx.save()
    ctx.strokeStyle = 'rgba(0, 0, 255, 0.5)'
    ctx.lineWidth = 1
    ctx.setLineDash([5, 3]) // línea discontinua
    ctx.strokeRect(
        box.minX,
        box.minY,
        box.maxX - box.minX,
        box.maxY - box.minY
    )
    ctx.restore()
}

/** Actualiza el bounding box de un shape tipo "freehand". */
function updateFreehandBoundingBox(shp) {
    let minX = Infinity, minY = Infinity
    let maxX = -Infinity, maxY = -Infinity
    for (const pt of shp.points) {
        if (pt.x < minX) minX = pt.x
        if (pt.x > maxX) maxX = pt.x
        if (pt.y < minY) minY = pt.y
        if (pt.y > maxY) maxY = pt.y
    }
    shp.boundingBox = { minX, minY, maxX, maxY }
}

/** Actualiza el bounding box de un shape tipo "text". */
function updateTextBoundingBox(shp) {
    ctx.save()
    ctx.font = shp.font
    // Medir el texto
    const metrics = ctx.measureText(shp.text)
    // El alto no siempre se obtiene directo, pero se puede aproximar
    const textHeight = parseInt(shp.font, 10) || 16

    const minX = shp.x
    // El texto se dibuja en la línea base, así que restamos un poco
    const minY = shp.y - textHeight
    const maxX = shp.x + metrics.width
    const maxY = shp.y

    shp.boundingBox = { minX, minY, maxX, maxY }
    ctx.restore()
}

/** Verifica si (x, y) está dentro del boundingBox (con tolerancia). */
function hitTestShape(x, y, shp) {
    if (!shp.boundingBox) return false
    const box = shp.boundingBox
    const inX = x >= (box.minX - selectionTolerance) && x <= (box.maxX + selectionTolerance)
    const inY = y >= (box.minY - selectionTolerance) && y <= (box.maxY + selectionTolerance)
    return inX && inY
}

/** Obtiene coordenadas correctas del canvas. */
function getCanvasCoordinates(e) {
    const rect = canvas.value.getBoundingClientRect()
    return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
    }
}

/** Registra puntos suavizados para un trazo más fino y estable. */
function pushStrokePoint(x, y, options = {}) {
    const { forceRaw = false } = options
    if (!currentStroke.length) {
        currentStroke.push({ x, y })
        return
    }

    const lastPoint = currentStroke[currentStroke.length - 1]
    const dx = x - lastPoint.x
    const dy = y - lastPoint.y
    const distance = Math.hypot(dx, dy)

    if (!forceRaw && distance < minPointGap) return

    const nextPoint = forceRaw
        ? { x, y }
        : {
            x: lastPoint.x + dx * smoothingFactor,
            y: lastPoint.y + dy * smoothingFactor
        }

    currentStroke.push(nextPoint)
}

// --- Algoritmo Douglas-Peucker ---
function simplifyDouglasPeucker(points, epsilon) {
    if (points.length < 3) return points

    let maxDist = 0
    let index = 0
    const end = points.length - 1

    for (let i = 1; i < end; i++) {
        const dist = perpendicularDistance(points[i], points[0], points[end])
        if (dist > maxDist) {
            maxDist = dist
            index = i
        }
    }

    if (maxDist > epsilon) {
        const left = simplifyDouglasPeucker(points.slice(0, index + 1), epsilon)
        const right = simplifyDouglasPeucker(points.slice(index), epsilon)
        return [...left.slice(0, -1), ...right]
    } else {
        return [points[0], points[end]]
    }
}

function perpendicularDistance(pt, lineStart, lineEnd) {
    const { x: x0, y: y0 } = pt
    const { x: x1, y: y1 } = lineStart
    const { x: x2, y: y2 } = lineEnd

    const num = Math.abs((y2 - y1) * x0 - (x2 - x1) * y0 + x2 * y1 - y2 * x1)
    const den = Math.sqrt((y2 - y1) ** 2 + (x2 - x1) ** 2)
    return den === 0 ? 0 : num / den
}

// --- Conversión Catmull-Rom → Bezier ---
function catmullRom2bezier(points) {
    const result = []
    for (let i = 0; i < points.length - 1; i++) {
        const p0 = i === 0 ? points[i] : points[i - 1]
        const p1 = points[i]
        const p2 = points[i + 1]
        const p3 = i + 2 < points.length ? points[i + 2] : p2

        const c1 = {
            x: p1.x + (p2.x - p0.x) / 6,
            y: p1.y + (p2.y - p0.y) / 6
        }
        const c2 = {
            x: p2.x - (p3.x - p1.x) / 6,
            y: p2.y - (p3.y - p1.y) / 6
        }
        result.push({ c1, c2, p2 })
    }
    return result
}

// --- Funciones de control (undo, redo, etc.) ---

function undo() {
    if (shapes.value.length > 0) {
        undoneShapes.value.push(shapes.value.pop())
        redrawCanvas()
    }
}

function redo() {
    if (undoneShapes.value.length > 0) {
        shapes.value.push(undoneShapes.value.pop())
        redrawCanvas()
    }
}

function clearCanvas() {
    shapes.value = []
    undoneShapes.value = []
    selectedShape = null
    currentStroke = []
    redrawCanvas()
}

function saveImage() {
    // Convierte el dibujo en JSON
    const shapesJSON = JSON.stringify(shapes.value)
    // Emite el evento hacia el padre con la información
    emit('saveShapes', shapesJSON)
}

function downloadImage() {
    const dataURL = canvas.value.toDataURL('image/png')
    const link = document.createElement('a')
    link.download = 'mi_dibujo.png'
    link.href = dataURL
    link.click()
}
</script>

<style scoped>
.canvas-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: 0.75rem;
}

.canvas-wrapper {
    position: relative;
    flex: 1;
    width: 100%;
    min-height: 300px;
    background: #fafafa;
    border-radius: 12px;
    overflow: hidden;
}

canvas {
    cursor: crosshair;
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

/* Barra de modos moderna */
.mode-bar {
    display: flex;
    gap: 0.5rem;
    padding: 0.4rem;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.mode-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.65rem 0.85rem;
    border: 2px solid transparent;
    border-radius: 10px;
    background: transparent;
    color: #64748b;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
}

.mode-btn i {
    font-size: 0.95rem;
}

.mode-btn:hover:not(.active) {
    background: #f8fafc;
    color: #475569;
    border-color: #e2e8f0;
}

.mode-btn.active {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border-color: #3b82f6;
    box-shadow: 0 4px 14px rgba(59, 130, 246, 0.35), 0 2px 4px rgba(59, 130, 246, 0.2);
    transform: translateY(-1px);
}

.mode-btn.active:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    box-shadow: 0 6px 18px rgba(59, 130, 246, 0.45), 0 2px 6px rgba(59, 130, 246, 0.25);
    transform: translateY(-2px);
}

/* Controles modernos */
.brush-controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    background: #ffffff;
    padding: 0.75rem 1rem;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.controls-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
}

.control-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.control-item label {
    font-size: 0.7rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Color picker moderno */
.color-input-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.color-picker {
    width: 36px;
    height: 36px;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
}

.color-picker:hover {
    border-color: #3b82f6;
    transform: scale(1.05);
}

.color-hex {
    font-size: 0.8rem;
    font-weight: 600;
    color: #475569;
    font-family: 'Courier New', monospace;
}

/* Slider moderno */
.slider-wrapper {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.line-slider {
    width: 100px;
    height: 5px;
    border-radius: 3px;
    background: linear-gradient(to right, #e2e8f0 0%, #3b82f6 100%);
    outline: none;
    -webkit-appearance: none;
}

.line-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.4);
    transition: all 0.2s;
}

.line-slider::-webkit-slider-thumb:hover {
    transform: scale(1.2);
    box-shadow: 0 3px 8px rgba(59, 130, 246, 0.6);
}

.line-slider::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.4);
    transition: all 0.2s;
}

.line-slider::-moz-range-thumb:hover {
    transform: scale(1.2);
    box-shadow: 0 3px 8px rgba(59, 130, 246, 0.6);
}

.value-display {
    min-width: 40px;
    font-size: 0.8rem;
    font-weight: 700;
    color: #334155;
    text-align: center;
}

/* Select moderno */
.font-select {
    padding: 0.45rem 0.65rem;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    background: #ffffff;
    color: #334155;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.font-select:hover {
    border-color: #3b82f6;
}

.font-select:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Botones de acción modernos */
.action-buttons {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    background: #ffffff;
    color: #64748b;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.action-btn:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #334155;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.action-btn:active:not(:disabled) {
    transform: translateY(0);
}

.action-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    background: #f8fafc;
}

.action-btn.primary {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-color: #10b981;
    color: white;
}

.action-btn.primary i {
    color: white;
}

.action-btn.primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
    border-color: #059669;
}

.action-btn.primary:hover:not(:disabled) i {
    color: white;
}

.action-btn.danger {
    color: #ef4444;
}

.action-btn.danger:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #fecaca;
    color: #dc2626;
}

.divider {
    width: 1px;
    height: 20px;
    background: #e2e8f0;
    margin: 0 0.2rem;
}

/* Responsive */
@media (max-width: 768px) {
    .brush-controls {
        flex-direction: column;
        gap: 1rem;
    }

    .controls-left {
        flex-wrap: wrap;
        justify-content: center;
    }

    .action-buttons {
        width: 100%;
        justify-content: center;
    }

    .mode-btn span {
        display: none;
    }
}

/* Estilos personalizados para SweetAlert2 */
:deep(.modern-swal-popup) {
    border-radius: 20px !important;
    padding: 32px !important;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
    font-family: inherit !important;
}

:deep(.modern-swal-title) {
    color: #1e293b !important;
    font-size: 28px !important;
    font-weight: 700 !important;
    margin-bottom: 24px !important;
}

:deep(.modern-swal-input) {
    border: 2px solid #e2e8f0 !important;
    border-radius: 12px !important;
    padding: 16px !important;
    font-size: 15px !important;
    line-height: 1.6 !important;
    color: #1e293b !important;
    transition: all 0.2s !important;
    font-family: inherit !important;
    resize: vertical !important;
}

:deep(.modern-swal-input:focus) {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1) !important;
    outline: none !important;
}

:deep(.modern-swal-actions) {
    margin-top: 28px !important;
    gap: 12px !important;
}

:deep(.modern-swal-confirm) {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
    color: white !important;
    border: none !important;
    padding: 14px 28px !important;
    border-radius: 12px !important;
    font-weight: 600 !important;
    font-size: 15px !important;
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    box-shadow: 0 4px 14px rgba(59, 130, 246, 0.4) !important;
    min-width: 120px !important;
}

:deep(.modern-swal-confirm:hover) {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
    transform: translateY(-2px) !important;
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5) !important;
}

:deep(.modern-swal-confirm:active) {
    transform: translateY(0) !important;
}

:deep(.modern-swal-cancel) {
    background: #f1f5f9 !important;
    color: #64748b !important;
    border: 2px solid #e2e8f0 !important;
    padding: 14px 28px !important;
    border-radius: 12px !important;
    font-weight: 600 !important;
    font-size: 15px !important;
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    min-width: 120px !important;
}

:deep(.modern-swal-cancel:hover) {
    background: #e2e8f0 !important;
    color: #475569 !important;
    border-color: #cbd5e1 !important;
    transform: translateY(-2px) !important;
}

:deep(.modern-swal-cancel:active) {
    transform: translateY(0) !important;
}

:deep(.modern-swal-success) {
    border-radius: 20px !important;
    padding: 28px !important;
}

:deep(.swal2-validation-message) {
    background: #fef2f2 !important;
    border: 2px solid #fca5a5 !important;
    color: #dc2626 !important;
    border-radius: 10px !important;
    padding: 12px 16px !important;
    font-weight: 600 !important;
    margin-top: 12px !important;
}

:deep(.swal2-icon.swal2-success) {
    border-color: #10b981 !important;
}

:deep(.swal2-icon.swal2-success [class^='swal2-success-line']) {
    background-color: #10b981 !important;
}

:deep(.swal2-icon.swal2-success .swal2-success-ring) {
    border-color: rgba(16, 185, 129, 0.3) !important;
}
</style>
